import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import en from '../locales/en.json';
import ar from '../locales/ar.json';
import hi from '../locales/hi.json';

const translations = { en, ar, hi };

// Fallback when COMMUNITY_CONFIG isn't available yet
const DEFAULT_LANGUAGES = [
  { code: 'en', label: 'English', labelNative: 'English', dir: 'ltr' },
];

// Metadata for known languages (label, native label, direction)
const LANGUAGE_REGISTRY = {
  en: { label: 'English', labelNative: 'English', dir: 'ltr' },
  ar: { label: 'Arabic', labelNative: 'العربية', dir: 'rtl' },
  hi: { label: 'Hindi', labelNative: 'हिन्दी', dir: 'ltr' },
  fr: { label: 'French', labelNative: 'Français', dir: 'ltr' },
  es: { label: 'Spanish', labelNative: 'Español', dir: 'ltr' },
  pt: { label: 'Portuguese', labelNative: 'Português', dir: 'ltr' },
  ur: { label: 'Urdu', labelNative: 'اردو', dir: 'rtl' },
  sw: { label: 'Swahili', labelNative: 'Kiswahili', dir: 'ltr' },
  zh: { label: 'Chinese', labelNative: '中文', dir: 'ltr' },
};

const LanguageContext = createContext();

/**
 * Reads languages ONLY from COMMUNITY_CONFIG.languages.
 * Format: [{ code: "ar", label: "Arabic", default: true }, ...]
 * English is always included as first entry.
 */
const getLanguagesFromConfig = () => {
  try {
    const communityLangs = window._lclx?.COMMUNITY_CONFIG?.languages;

    if (!Array.isArray(communityLangs) || communityLangs.length === 0) {
      return { languages: DEFAULT_LANGUAGES, defaultCode: null };
    }

    const result = [{ code: 'en', label: 'English', labelNative: 'English', dir: 'ltr' }];
    const seen = new Set(['en']);
    let defaultCode = null;

    for (const lang of communityLangs) {
      const code = lang.code;
      if (!code) continue;

      if (lang.default === true && !defaultCode) {
        defaultCode = code;
      }

      if (seen.has(code)) continue;
      seen.add(code);

      const registry = LANGUAGE_REGISTRY[code];
      result.push({
        code,
        label: registry?.label || lang.label || code,
        labelNative: registry?.labelNative || lang.label || code,
        dir: registry?.dir || 'ltr',
      });
    }

    return { languages: result, defaultCode };
  } catch (e) {
    return { languages: DEFAULT_LANGUAGES, defaultCode: null };
  }
};

/**
 * Priority:
 *   1. localStorage (user's saved preference — persists across reloads/revisits)
 *   2. COMMUNITY_CONFIG default language (if a language has default: true)
 *   3. English fallback
 *
 * This is only a best-effort guess for the initial render — COMMUNITY_CONFIG is
 * loaded asynchronously and is almost never available yet at mount time. The
 * `checkConfig` poll in LanguageProvider re-evaluates the same 3-tier priority
 * against the real community language list once it loads, and is authoritative.
 */
const getInitialLanguage = () => {
  // 1. User preference from localStorage
  const stored = localStorage.getItem('kwantu_language');
  if (stored && translations[stored]) return stored;

  // 2. Config default from COMMUNITY_CONFIG
  const { defaultCode } = getLanguagesFromConfig();
  if (defaultCode && translations[defaultCode]) return defaultCode;

  // 3. English fallback
  return 'en';
};

/** Sync language code to all legacy globals so getNameByLang / service.getLanguage() work */
const syncLegacyGlobals = (code) => {
  try {
    if (window.app) {
      window.app.preferredLanguage = { community: '', lang: code };
    }
    if (window._lclx) {
      if (!window._lclx.userPreferences) window._lclx.userPreferences = {};
      if (!window._lclx.userPreferences.language) window._lclx.userPreferences.language = {};
      window._lclx.userPreferences.language.code = code;
    }
  } catch (e) { /* ignore */ }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);
  const [configLanguages, setConfigLanguages] = useState(() => getLanguagesFromConfig().languages);

  // Poll for COMMUNITY_CONFIG (loads async, and can change as the user moves
  // between communities). Once available/updated, refresh the language list and
  // re-resolve the active language against the 3-tier priority below.
  useEffect(() => {
    const checkConfig = () => {
      const { languages: updated, defaultCode } = getLanguagesFromConfig();

      setConfigLanguages(prev => {
        const prevCodes = prev.map(l => l.code).join(',');
        const newCodes = updated.map(l => l.code).join(',');
        return prevCodes !== newCodes ? updated : prev;
      });

      // Re-evaluate the 3-tier priority against the CURRENT community's language
      // list on every tick: 1) stored preference, but only if it's actually in
      // this community's list; 2) this community's default; 3) English.
      const stored = localStorage.getItem('kwantu_language');
      const storedValid = stored && translations[stored] && updated.some(l => l.code === stored);

      let resolved;
      if (storedValid) {
        resolved = stored;
      } else if (defaultCode && translations[defaultCode] && updated.some(l => l.code === defaultCode)) {
        resolved = defaultCode;
      } else {
        resolved = 'en';
      }

      setLanguageState(prev => prev !== resolved ? resolved : prev);
    };

    checkConfig();
    const interval = setInterval(checkConfig, 2000);
    return () => clearInterval(interval);
  }, []);

  const supportedLanguages = useMemo(() => configLanguages, [configLanguages]);

  const isRTL = useMemo(() => {
    const langInfo = supportedLanguages.find(l => l.code === language);
    return langInfo?.dir === 'rtl';
  }, [language, supportedLanguages]);

  // User switches language: save to localStorage + full page reload
  const setLanguage = useCallback((code) => {
    if (!translations[code]) {
      console.warn(`No translations for language: ${code}, falling back to English`);
    }

    // Save to localStorage — this persists across reloads and revisits
    localStorage.setItem('kwantu_language', code);
    syncLegacyGlobals(code);

    // Full page reload so ALL parts (React, Knockout/Wijmo forms, legacy templates)
    // pick up the new language top to bottom
    window.location.reload();
  }, []);

  // Keep <html> attributes and legacy globals in sync on every language change
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('lang', language);
    html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    document.body.style.direction = isRTL ? 'rtl' : 'ltr';
    document.body.style.textAlign = isRTL ? 'right' : 'left';
    syncLegacyGlobals(language);
  }, [language, isRTL]);

  // Translation function with fallback to English then key
  const t = useCallback((key, params) => {
    let str = translations[language]?.[key] || translations['en']?.[key] || key;

    if (params && typeof str === 'string') {
      Object.keys(params).forEach(param => {
        str = str.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
      });
    }
    return str;
  }, [language]);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      isRTL,
      t,
      supportedLanguages,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export default LanguageContext;
